import { Avatar, Col, Progress, Row, Select, Tag, Typography } from "antd";
import { useCallback, useEffect, useState } from "react";
import { Loader } from "shared/components";
import {
  SearchOutlined,
  CloseCircleOutlined,
  UserOutlined,
} from "@ant-design/icons";
import "./ArtistComparator.scss";
import { debounce, some } from "lodash";
import { compareArtists, fetchArtistByName } from "radar/Service/RadarService";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { getI18nGenre } from "shared/helpers/i18nGenre";
import { useHistory } from "react-router-dom";
import careerStages from "shared/constants/careerStages";
import { ReactComponent as CompareIcon } from "shared/assets/images/compare-icon.svg";
import PlusButton from "./PlusButtonComparator";

interface ArtistComparatorProps {
  onCompare?: (artist: any) => void;
  onClear?: () => void;
  exporting?: boolean;
}

const srcToBase64 = (src: string): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.setAttribute("crossOrigin", "anonymous");
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.drawImage(img, 0, 0);
        const dataURL = canvas.toDataURL("image/png");
        resolve(dataURL);
      } else {
        reject("Error");
      }
    };
    img.src = src;
  });
};

const ArtistComparator: React.FC<ArtistComparatorProps> = (props) => {
  const { onCompare, onClear, exporting = false } = props;
  const history = useHistory();
  const [artists, setArtists] = useState<any[]>([]);
  const [artistImage, setArtistImage] = useState<any[]>([]);
  const [searchedArtists, setSearchedArtists] = useState([]);
  const [artistsOptions, setArtistsOptions] = useState<any[]>([]);
  const [artistsStage, setArtistsStage] = useState<any>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const { t, i18n } = useTranslation("radar");
  const genres = useSelector((state: any) => state.genres.genres);
  const wrappGetI18nGenre = (genre: string) =>
    getI18nGenre(genre, i18n, genres);

  const onAddArtist = () => {
    setArtists([...artists, { noArtist: true }]);
  };

  const onChangeArtist = async (value: any, index: number) => {
    const artist: any = searchedArtists.find(
      (opt: any) => opt.artistIdStrm === value
    );
    if (artist) {
      const artistCopy = [...artists];
      artistCopy[index] = artist;
      localStorage.setItem(
        "RADAR_ARTIST_COMPARISON",
        JSON.stringify(artistCopy)
      );
      setArtists(artistCopy);
    }
  };

  const onRemoveClick = (artist: any) => {
    const artistCopy = artists.filter((art) => art !== artist);
    localStorage.setItem("RADAR_ARTIST_COMPARISON", JSON.stringify(artistCopy));
    setArtists(artistCopy);
  };

  const getArtists = async (artistNameTerm: string) => {
    if (artistNameTerm.replace(/[/\\#,+()$~%.'":*?<>{}]/g, "")) {
      setLoading(true);
      setArtistsOptions([]);
      const response = await fetchArtistByName({ artistNameTerm });
      setSearchedArtists(response);
      setLoading(false);
    }
  };

  const getArtistComparison = useCallback(async () => {
    const response = await compareArtists({
      artistIds: artists.map(({ artistIdStrm }) => artistIdStrm),
    });
    const artStage = response.map(
      ({
        artistMetadata: { artistIdStrm },
        strmIndexes: { careerStage, stageProgress },
      }: any) => {
        return {
          careerStage,
          artistIdStrm,
          stageProgress,
        };
      }
    );
    setArtistsStage(artStage);
    if (onCompare) onCompare(response);
  }, [artists, onCompare]);

  const getArtistsStageInfo = (artistId: string) => {
    if (artistId && artistsStage.length) {
      const artist = artistsStage.find(
        ({ artistIdStrm }: any) => artistIdStrm === artistId
      );
      return (
        (artist?.careerStage &&
          `${t("components.ArtistComparator.stage")} ${t(
            `pages.Radar.${careerStages[artist.careerStage - 1 || 0]}`
          )}`) ||
        ""
      );
    }
    return "";
  };

  const getArtistStageProgresss = (artistId: string) => {
    if (artistId && artistsStage.length) {
      const artist = artistsStage.find(
        ({ artistIdStrm }: any) => artistIdStrm === artistId
      );
      return artist?.stageProgress || 0;
    }
    return 0;
  };

  useEffect(() => {
    setArtistsOptions(
      searchedArtists.map(
        ({ artistIdStrm, artistName, imagesSpotify }: any) => ({
          id: artistIdStrm,
          name: artistName,
          image: (imagesSpotify && imagesSpotify[2]?.url) || "",
        })
      )
    );
  }, [searchedArtists]);

  useEffect(() => {
    if (artists.length > 0 && !some(artists, (artist) => artist.noArtist)) {
      getArtistComparison();
    }
    if (artists.length === 0 && onClear) onClear();
  }, [artists, onClear, getArtistComparison]);

  useEffect(() => {
    const savedArtists = localStorage.getItem("RADAR_ARTIST_COMPARISON");
    if (savedArtists) setArtists(JSON.parse(savedArtists));
  }, []);

  useEffect(() => {
    const extractArtistImage = async () => {
      setArtistImage(
        await Promise.all(
          artists.map(
            async (artist: any) =>
              await srcToBase64(
                (artist.imagesSpotify && artist.imagesSpotify[2]?.url) || ""
              )
          )
        )
      );
    };
    extractArtistImage();
  }, [artists]);

  if (exporting)
    return (
      <Row gutter={16} justify="center" className="artist-comparator">
        {artists.map((artist: any, index: number, list: any) => (
          <>
            <Col style={{ width: 180 }} key={artist.artistIdStrm}>
              <div className="card-comparison" style={{ minHeight: "300px" }}>
                <Row justify="center">
                  <span
                    style={{
                      fontSize: 16,
                      margin: "16px 0 ",
                      textTransform: "capitalize",
                    }}
                  >
                    {artist.artistName}
                  </span>
                  <Col xs={24}>
                    <Avatar
                      src={artistImage[index]}
                      shape="square"
                      style={{ backgroundColor: "#1b1b1b" }}
                      icon={<UserOutlined />}
                      size={100}
                    />
                  </Col>
                  <Col xs={24}>
                    <span style={{ fontWeight: "bold", fontSize: 16 }}>
                      {artist.country}
                    </span>
                  </Col>
                  <Col xs={24}>
                    {artist.genresStrm?.map((genre: any) => (
                      <Tag
                        key={Math.random()}
                        color="transparent"
                        style={{
                          border: "2px solid white",
                          borderRadius: 12,
                          padding: "2px 4px",
                          fontSize: 8,
                        }}
                      >
                        {wrappGetI18nGenre(genre)}
                      </Tag>
                    ))}
                  </Col>
                  <Col xs={24}>
                    <strong style={{ fontSize: 8 }}>
                      {getArtistsStageInfo(artist.artistIdStrm)}
                    </strong>
                  </Col>
                  <Col xs={24} hidden={artist.noArtist}>
                    <div style={{ margin: "0 16px" }}>
                      <Progress
                        strokeColor="#455FE4"
                        style={{ color: "white" }}
                        percent={Math.ceil(
                          getArtistStageProgresss(artist.artistIdStrm) * 100 ||
                            0
                        )}
                      />
                    </div>
                  </Col>
                </Row>
              </div>
            </Col>
            {index + 1 < list.length && (
              <div
                style={{
                  height: "300px",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <CompareIcon height={20} width={20} />
              </div>
            )}
          </>
        ))}
      </Row>
    );

  return (
    <Row gutter={32} justify="center" className="artist-comparator">
      {artists.map((artist: any, index: number, list: any) => (
        <>
          <Col xs={4} key={artist.artistIdStrm}>
            <div className="card-comparison" style={{ minHeight: "350px" }}>
              <Row justify="center">
                <Col xs={24} style={{ display: "block" }}>
                  <Typography.Title
                    style={{
                      cursor: artist?.artistIdStrm ? "pointer" : "default",
                      fontSize: 16,
                      margin: "16px 0 ",
                    }}
                    onClick={() =>
                      artist.artistIdStrm &&
                      history.push("/artist-profile", {
                        artist: artist,
                      })
                    }
                  >
                    {artist.artistName ||
                      t("components.ArtistComparator.pleaseSelect")}
                  </Typography.Title>
                  <CloseCircleOutlined
                    style={{ position: "absolute", top: "5px", right: "5px" }}
                    onClick={() => onRemoveClick(artist)}
                  />
                </Col>
                <Col
                  xs={24}
                  onClick={() =>
                    artist.artistIdStrm &&
                    history.push("/artist-profile", {
                      artist: artist,
                    })
                  }
                >
                  <Avatar
                    src={
                      (artist.imagesSpotify && artist.imagesSpotify[0]?.url) ||
                      ""
                    }
                    shape="square"
                    style={{ backgroundColor: "#1b1b1b" }}
                    icon={<UserOutlined />}
                    size={150}
                  />
                </Col>
                <Col xs={24}>
                  <span style={{ fontWeight: "bold", fontSize: 18 }}>
                    {artist.country}
                  </span>
                </Col>
                <Col xs={24}>
                  {artist.genresStrm?.map((genre: any) => (
                    <Tag
                      key={Math.random()}
                      color="transparent"
                      style={{
                        border: "2px solid white",
                        borderRadius: 12,
                        padding: "6px 10px",
                      }}
                    >
                      {wrappGetI18nGenre(genre)}
                    </Tag>
                  ))}
                </Col>
                <Col xs={24}>
                  <strong>{getArtistsStageInfo(artist.artistIdStrm)}</strong>
                </Col>
                <Col xs={24} hidden={artist.noArtist}>
                  <div style={{ margin: "0 16px" }}>
                    <Progress
                      strokeColor="#455FE4"
                      style={{ color: "white" }}
                      percent={Math.ceil(
                        getArtistStageProgresss(artist.artistIdStrm) * 100 || 0
                      )}
                    />
                  </div>
                </Col>
                <Col
                  xs={24}
                  hidden={!artist.noArtist}
                  style={{ padding: "0 16px" }}
                >
                  <Select
                    showSearch
                    filterOption={false}
                    suffixIcon={<SearchOutlined />}
                    onSearch={debounce(getArtists, 1000)}
                    onSelect={(value: any) => onChangeArtist(value, index)}
                    style={{ width: "100% " }}
                    notFoundContent={
                      <Loader.NotFoundLoader loading={loading} />
                    }
                  >
                    {artistsOptions.map((artist: any) => (
                      <Select.Option value={artist.id} key={artist.id}>
                        {artist.image && (
                          <Avatar
                            style={{ marginRight: "6px" }}
                            src={artist.image}
                          />
                        )}
                        {artist.name}
                      </Select.Option>
                    ))}
                  </Select>
                </Col>
              </Row>
            </div>
          </Col>
          {index + 1 < list.length && (
            <div
              style={{
                height: "300px",
                display: "flex",
                alignItems: "center",
              }}
            >
              <CompareIcon height={20} width={20} />
            </div>
          )}
        </>
      ))}
      {artists.length < 5 && <PlusButton onAdd={onAddArtist} />}
    </Row>
  );
};

export default ArtistComparator;
